// src/pages/doctors/dashboard/index.jsx
import React from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import { useNavigate } from "react-router-dom";

import { supabase } from "@/services/supabaseClient";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import PrescriptionPatientCell from "@/components/prescriptions/PrescriptionPatientCell.jsx";
import AfrimedSearchBar from "@/components/ui/AfrimedSearchBar.jsx";
import Card from "@/components/ui/Card.jsx";
import {
  countPrescriptionsLast30Days,
  listDoctorPrescriptions,
} from "@/services/doctorPrescriptions.js";
import {
  PATIENT_AVATAR_PLACEHOLDER,
  loadClinicianAppointmentPatientIdentities,
  loadPatientIdentityContextsByAppointmentId,
} from "@/services/patientIdentityService.js";
import { pickDiagnosisTitle } from "@/utils/diagnosis.js";
import {
  countUniqueAppointmentPatients,
  resolveLast30DaysWindow,
} from "@/lib/doctorPatientStats.js";

// ===== Icons (Dashboard) =====
const ICON_PATIENTS = "/icons/appointments/dashboard/injured_patient.svg";
const ICON_RATING = "/icons/appointments/dashboard/star.svg";
const ICON_APPOINTMENTS = "/icons/appointments/dashboard/appointments.svg";
const ICON_PRESCRIPTIONS = "/icons/appointments/dashboard/e-prescriptions.svg";

const ICON_BADGE_UP = "/icons/appointments/dashboard/arrow_circle_up.svg";
const ICON_CALENDAR = "/icons/appointments/dashboard/calendar.svg";
const ICON_EXPAND_MORE = "/icons/appointments/dashboard/expand_more.svg";
const NAVIGATE_LEFT = "/icons/appointments/dashboard/navigate_left.svg";
const NAVIGATE_NEXT = "/icons/appointments/dashboard/navigate_next.svg";

const ICON_LEGEND_APPOINTMENT =
  "/icons/appointments/dashboard/radio_button_checked_fill_blue.svg";
const ICON_LEGEND_EVISIT =
  "/icons/appointments/dashboard/radio_button_checked_fill.svg";
const ICON_LEGEND_PENDING =
  "/icons/appointments/dashboard/radio_button_checked_fill_grey.svg";
const ICON_RISK_NORMAL = "/icons/appointments/dashboard/radio_button_normal.svg";
const ICON_RISK_CAUTION = "/icons/appointments/dashboard/radio_button_caution.svg";
const ICON_RISK_CRITICAL = "/icons/appointments/dashboard/radio_button_critical.svg";
const CHART_LINE_COLOR = "rgb(var(--afrimed-secondary))";

const BADGE_BG = "bg-[rgba(0,189,196,0.12)]";
const BADGE_TEXT = "text-[#008A8F]";

const RANGE_OPTIONS = [
  { key: "Today", i18n: "doctorDashboard.range.today", fallback: "Today" },
  { key: "Week", i18n: "doctorDashboard.range.week", fallback: "Week" },
  { key: "Month", i18n: "doctorDashboard.range.month", fallback: "Month" },
  { key: "Year", i18n: "doctorDashboard.range.year", fallback: "Year" },
];
const VISITOR_RANGE_OPTIONS = [
  { key: "3m", i18n: "doctorDashboard.visitors.range.threeMonths", fallback: "Past 3 months", months: 3 },
  { key: "6m", i18n: "doctorDashboard.visitors.range.sixMonths", fallback: "Past 6 months", months: 6 },
  { key: "12m", i18n: "doctorDashboard.visitors.range.year", fallback: "Past Year", months: 12 },
];

function resolveUiLocale(languageCode) {
  if (languageCode === "sn") return "sn-ZW";
  if (languageCode === "zu") return "zu-ZA";
  return languageCode || undefined;
}

const CUSTOM_LOCALE_LABELS = {
  sn: {
    monthsShort: ["Nd", "Kk", "Km", "Kb", "Cv", "Ck", "Cg", "Ny", "Gu", "Gm", "Mb", "Zv"],
    monthsLong: [
      "Ndira",
      "Kukadzi",
      "Kurume",
      "Kubvumbi",
      "Chivabvu",
      "Chikumi",
      "Chikunguru",
      "Nyamavhuvhu",
      "Gunyana",
      "Gumiguru",
      "Mbudzi",
      "Zvita",
    ],
    weekdaysNarrow: ["Sv", "Mu", "Cp", "Ct", "Cn", "Cs", "Mg"],
  },
  zu: {
    monthsShort: ["Jan", "Feb", "Mas", "Eph", "Mey", "Jun", "Jul", "Aga", "Sep", "Okt", "Nov", "Dis"],
    monthsLong: [
      "Januwari",
      "Febhuwari",
      "Mashi",
      "Ephreli",
      "Meyi",
      "Juni",
      "Julayi",
      "Agasti",
      "Septhemba",
      "Okthoba",
      "Novemba",
      "Disemba",
    ],
    weekdaysNarrow: ["Son", "Mso", "Bil", "Tha", "Sin", "Hla", "Mgq"],
  },
};

function toDate(value) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
}

function endOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);
}

function monthKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function formatMonthLabel(date, languageCode, localeCode) {
  const custom = CUSTOM_LOCALE_LABELS[languageCode];
  if (custom) return custom.monthsShort[date.getMonth()];
  return new Intl.DateTimeFormat(localeCode, { month: "short" }).format(date);
}

function formatMonthLong(date, languageCode, localeCode) {
  const custom = CUSTOM_LOCALE_LABELS[languageCode];
  if (custom) return `${custom.monthsLong[date.getMonth()]} ${date.getFullYear()}`;
  return new Intl.DateTimeFormat(localeCode, { month: "long", year: "numeric" }).format(date);
}

function parseMonthKey(key) {
  const [yearRaw, monthRaw] = String(key || "").split("-");
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  if (!Number.isFinite(year) || !Number.isFinite(month)) return null;
  if (month < 1 || month > 12) return null;
  return new Date(year, month - 1, 1);
}

function formatTooltipDate(monthKeyValue, languageCode, localeCode) {
  const d = parseMonthKey(monthKeyValue);
  if (!d) return "-";
  try {
    const custom = CUSTOM_LOCALE_LABELS[languageCode];
    if (custom) {
      return `${String(d.getDate()).padStart(2, "0")} ${custom.monthsShort[d.getMonth()]} ${d.getFullYear()}`;
    }
    return new Intl.DateTimeFormat(localeCode, {
      month: "short",
      day: "2-digit",
      year: "numeric",
    }).format(d);
  } catch {
    return "-";
  }
}

function formatTrend(current, previous) {
  const curr = Number(current || 0);
  const prev = Number(previous || 0);

  if (prev <= 0) {
    if (curr <= 0) return "0%";
    return "+100%";
  }

  const raw = ((curr - prev) / prev) * 100;
  const rounded = Math.round(raw);
  const sign = rounded > 0 ? "+" : "";
  return `${sign}${rounded}%`;
}

function formatTooltipTrend(current, previous) {
  const curr = Number(current || 0);
  const prev = Number(previous || 0);
  if (prev <= 0) {
    if (curr <= 0) return "0 %";
    return "100 %";
  }
  const raw = ((curr - prev) / prev) * 100;
  return `${Math.abs(Math.round(raw))} %`;
}

function buildMonthlySeries(rows, now = new Date(), languageCode, localeCode) {
  const base = startOfMonth(now);
  const buckets = [];
  const indexByKey = new Map();

  for (let i = 11; i >= 0; i -= 1) {
    const d = new Date(base.getFullYear(), base.getMonth() - i, 1);
    const key = monthKey(d);
    const idx = buckets.length;
    indexByKey.set(key, idx);
    buckets.push({
      key,
      month: formatMonthLabel(d, languageCode, localeCode),
      appointments: 0,
      eVisits: 0,
      pending: 0,
    });
  }

  for (const row of rows) {
    const d = toDate(row?.appointment_date);
    if (!d) continue;
    const key = monthKey(d);
    const idx = indexByKey.get(key);
    if (typeof idx !== "number") continue;

    buckets[idx].appointments += 1;
    if (String(row?.consultation_type || "").toLowerCase() === "private") {
      buckets[idx].eVisits += 1;
    }
    if (["pending", "scheduled"].includes(String(row?.status || "").toLowerCase())) {
      buckets[idx].pending += 1;
    }
  }

  return buckets;
}

function filterByRange(rows, range, now = new Date()) {
  let from = null;
  let to = null;

  if (range === "Today") {
    from = startOfDay(now);
    to = endOfDay(now);
  } else if (range === "Week") {
    from = startOfDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6));
    to = endOfDay(now);
  } else if (range === "Month") {
    from = startOfMonth(now);
    to = endOfDay(now);
  } else if (range === "Year") {
    from = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
    to = endOfDay(now);
  }

  if (!from || !to) return rows;

  return rows.filter((row) => {
    const d = toDate(row?.appointment_date);
    if (!d) return false;
    return d >= from && d <= to;
  });
}

function fmtShortDate(iso, languageCode, localeCode) {
  const d = toDate(iso);
  if (!d) return "-";
  try {
    const custom = CUSTOM_LOCALE_LABELS[languageCode];
    if (custom) {
      return `${String(d.getDate()).padStart(2, "0")} ${custom.monthsShort[d.getMonth()]} ${d.getFullYear()}`;
    }
    return new Intl.DateTimeFormat(localeCode, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(d);
  } catch {
    return "-";
  }
}

function toArray(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
}

function ageFromDateOfBirth(value) {
  const dob = toDate(value);
  if (!dob) return null;
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const monthDelta = now.getMonth() - dob.getMonth();
  if (monthDelta < 0 || (monthDelta === 0 && now.getDate() < dob.getDate())) {
    age -= 1;
  }
  return age >= 0 ? age : null;
}

function deriveRiskLevel(conditionsValue) {
  const normalized = toArray(conditionsValue).map((item) => item.toLowerCase());
  if (normalized.length === 0) return "Normal";

  const criticalSignals = [
    "cancer",
    "stroke",
    "heart failure",
    "kidney failure",
    "hiv",
    "tuberculosis",
    "tb",
  ];
  if (normalized.some((item) => criticalSignals.some((signal) => item.includes(signal)))) {
    return "Critical";
  }

  const cautionSignals = [
    "asthma",
    "diabetes",
    "hypertension",
    "arthritis",
    "psoriasis",
    "cholesterol",
    "thyroid",
  ];
  if (normalized.some((item) => cautionSignals.some((signal) => item.includes(signal)))) {
    return "Caution";
  }

  return "Normal";
}

function resolvePatientName(row = {}) {
  const display = String(row?.display_name || "").trim();
  if (display) return display;
  const full = String(row?.full_name || "").trim();
  if (full) return full;
  const joined = [row?.first_name, row?.last_name].filter(Boolean).join(" ").trim();
  if (joined) return joined;
  return "";
}

function readDoctorAppointmentDraft(appointmentId) {
  if (typeof window === "undefined" || !appointmentId) return null;
  const key = `afrimed:doctor_appt:${String(appointmentId)}`;

  try {
    const sessionRaw = window.sessionStorage.getItem(key);
    if (sessionRaw) return JSON.parse(sessionRaw);
  } catch {}

  try {
    const localRaw = window.localStorage.getItem(key);
    return localRaw ? JSON.parse(localRaw) : null;
  } catch {
    return null;
  }
}

function deriveAppointmentCondition(row) {
  const appointmentDraft = readDoctorAppointmentDraft(String(row?.id || "").trim());

  return (
    pickDiagnosisTitle(
      row?.diagnosis_title,
      appointmentDraft?.diagnosisTitle,
      appointmentDraft?.diagnosis,
    ) || ""
  );
}

function MetricIconPill({
  iconSrc,
  iconSize = 20,
  nudgeX = 0,
  nudgeY = 0,
}) {
  return (
    <div
      className="
        h-7 w-7
        rounded-[10px]
        bg-[rgba(24,50,119,0.06)]
        flex items-center justify-center
        shrink-0
      "
      aria-hidden="true"
    >
      <img
        src={iconSrc}
        alt=""
        draggable="false"
        className="block object-contain opacity-90"
        style={{
          width: iconSize,
          height: iconSize,
          transform: `translate(${nudgeX}px, ${nudgeY}px)`,
        }}
      />
    </div>
  );
}

function PercentBadge({ value = "10%" }) {
  const normalized = String(value || "").trim();
  const showIcon = normalized !== "-" && normalized.endsWith("%");

  return (
    <span
      className={[
        "inline-flex items-center gap-1 rounded-full px-2 py-1",
        BADGE_BG,
        BADGE_TEXT,
        "text-label-small leading-none",
      ].join(" ")}
    >
      {showIcon ? (
        <img src={ICON_BADGE_UP} alt="" className="h-4 w-4" draggable="false" />
      ) : null}
      <span>{normalized || "-"}</span>
    </span>
  );
}

function MetricCard({
  iconSrc,
  iconSize = 20,
  iconNudgeX = 0,
  iconNudgeY = 0,
  label,
  value,
  footerLabel,
  footerPct = "10%",
  onClick,
}) {
  return (
    <Card
      as={onClick ? "button" : "div"}
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className="
        p-4
        text-left
        w-full
      "
    >
      <div className="flex items-center gap-2">
        {iconSrc ? (
          <MetricIconPill
            iconSrc={iconSrc}
            iconSize={iconSize}
            nudgeX={iconNudgeX}
            nudgeY={iconNudgeY}
          />
        ) : null}
        <div className="text-body-medium text-[rgb(var(--clr-on-surface-variant))]">
          {label}
        </div>
      </div>

      <div className="mt-3 text-headline-small text-[rgb(var(--clr-on-surface))]">
        {value}
      </div>

      {footerLabel ? (
        <div className="mt-4 flex items-center gap-3">
          <span className="text-body-small text-[rgb(var(--clr-on-surface-variant))]">
            {footerLabel}
          </span>
          <PercentBadge value={footerPct} />
        </div>
      ) : null}
    </Card>
  );
}

function RangePill({ label, expanded = false, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-haspopup="menu"
      aria-expanded={expanded}
      className="
        inline-flex items-center gap-2
        h-11
        rounded-[12px]
        pl-5 pr-4
        border border-[rgba(24,50,119,0.10)]
        bg-[rgb(var(--clr-surface-cont-highest))]
        shadow-[0px_0px_0px_1px_rgba(24,50,119,0.10),0px_1.5px_0px_-0.5px_rgba(26,27,35,0.06)]
        text-title-small text-[rgb(var(--clr-on-surface-variant))]
      "
      aria-label={label}
      title={label}
    >
      <img
        src={ICON_CALENDAR}
        alt=""
        className="h-[20px] w-[20px] opacity-90"
        draggable="false"
      />
      <span className="whitespace-nowrap">{label}</span>
      <img
        src={ICON_EXPAND_MORE}
        alt=""
        className={["h-[20px] w-[20px] opacity-80 transition-transform", expanded ? "rotate-180" : ""].join(" ")}
        draggable="false"
      />
    </button>
  );
}

function MonthDropdown({ label, expanded = false, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-haspopup="menu"
      aria-expanded={expanded}
      className="
        inline-flex items-center gap-2
        text-label-large text-[rgb(var(--clr-on-surface-variant))]
      "
      aria-label={label}
      title={label}
    >
      <span className="whitespace-nowrap">{label}</span>
      <img
        src={ICON_EXPAND_MORE}
        alt=""
        className={["h-4 w-4 opacity-80 transition-transform", expanded ? "rotate-180" : ""].join(" ")}
        draggable="false"
      />
    </button>
  );
}

function LegendBadge({ iconSrc, label }) {
  return (
    <div className="inline-flex items-center gap-2">
      <img src={iconSrc} alt="" className="h-[18px] w-[18px]" draggable="false" />
      <span className="text-body-small text-[rgb(var(--clr-on-surface-variant))]">
        {label}
      </span>
    </div>
  );
}

function RiskPill({ level }) {
  const { t } = useLanguage();
  const normalized = String(level || "Normal").toLowerCase();
  const byLevel = {
    normal: {
      wrap: "bg-[rgba(78,131,255,0.12)] text-[#3B72E8]",
      icon: ICON_RISK_NORMAL,
      label: t("doctorDashboard.risk.normal", "Normal"),
    },
    caution: {
      wrap: "bg-[rgba(235,161,43,0.16)] text-[#C97100]",
      icon: ICON_RISK_CAUTION,
      label: t("doctorDashboard.risk.caution", "Caution"),
    },
    critical: {
      wrap: "bg-[rgba(233,90,75,0.14)] text-[#C53728]",
      icon: ICON_RISK_CRITICAL,
      label: t("doctorDashboard.risk.critical", "Critical"),
    },
  };
  const style = byLevel[normalized] || byLevel.normal;

  return (
    <span
      className={[
        "inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-label-small leading-none",
        style.wrap,
      ].join(" ")}
    >
      <img src={style.icon} alt="" className="h-[18px] w-[18px] shrink-0" draggable="false" />
      <span>{style.label}</span>
    </span>
  );
}

function VisitorsTooltip({ active, payload }) {
  const { languageCode } = useLanguage();
  const localeCode = resolveUiLocale(languageCode);
  if (!active || !Array.isArray(payload) || payload.length === 0) return null;
  const item = payload[0]?.payload || {};
  const current = Number(item?.appointments || 0);
  const previous = Number(item?.prevAppointments || 0);
  const positive = current >= previous;
  const trend = formatTooltipTrend(current, previous);

  return (
    <div
      className="
        rounded-[16px]
        bg-[rgb(var(--clr-surface-container-low))]
        p-[10px]
        border border-[rgba(24,50,119,0.10)]
        shadow-[0px_1.5px_0px_-0.5px_rgba(26,27,35,0.06),0px_3px_3px_-1.5px_rgba(26,27,35,0.06),0px_3px_6px_-3px_rgba(26,27,35,0.06),0px_6px_12px_-6px_rgba(29,79,215,0.06)]
      "
    >
      <div className="flex flex-col items-start gap-2">
        <div className="text-label-medium text-[rgb(var(--clr-on-surface-variant))] opacity-60">
          {formatTooltipDate(item?.key, languageCode, localeCode)}
        </div>
        <div className="text-title-medium text-[rgb(var(--clr-on-surface))]">{current}</div>

        <span
          className="
            inline-flex items-center gap-[2px]
            h-6 rounded-[8px]
            pl-1 pr-2
            bg-[rgba(0,189,196,0.12)]
            text-[#008A8F]
            text-body-medium
          "
        >
          {positive ? (
            <img src={ICON_BADGE_UP} alt="" className="h-4 w-4" draggable="false" />
          ) : null}
          <span>{trend}</span>
        </span>
      </div>
    </div>
  );
}

function VisitorYAxisTick({ x = 0, y = 0, payload }) {
  return (
    <text
      x={x - 2}
      y={y + 4}
      fill="rgba(91,107,134,1)"
      fontSize={12}
      textAnchor="end"
    >
      {payload?.value ?? ""}
    </text>
  );
}

function CalendarNavArrows({ onPrev, onNext }) {
  const { t } = useLanguage();
  const btn =
    "h-9 w-9 rounded-[12px] grid place-items-center hover:bg-[rgba(var(--clr-on-surface),0.08)]";
  return (
    <div className="flex items-center gap-2">
      <button type="button" className={btn} aria-label={t("doctorDashboard.calendar.previousMonth", "Previous month")} onClick={onPrev}>
        <img src={NAVIGATE_LEFT} alt="" className="h-6 w-6 opacity-80" draggable="false" />
      </button>
      <button type="button" className={btn} aria-label={t("doctorDashboard.calendar.nextMonth", "Next month")} onClick={onNext}>
        <img src={NAVIGATE_NEXT} alt="" className="h-6 w-6 opacity-80" draggable="false" />
      </button>
    </div>
  );
}

function MobileField({ label, children }) {
  return (
    <div>
      <div className="text-label-small text-[rgb(var(--clr-on-surface-variant))]">{label}</div>
      <div className="mt-0.5 text-body-medium text-[rgb(var(--clr-on-surface))]">{children}</div>
    </div>
  );
}

function SegmentedControl({ active, onChange }) {
  const { t } = useLanguage();
  return (
    <div
      className="
        inline-flex items-center
        rounded-[10px]
        border border-[rgb(var(--clr-outline-variant))]
        bg-[rgba(var(--clr-on-surface),0.08)]
        p-[1px]
        overflow-hidden
      "
      role="tablist"
      aria-label={t("doctorDashboard.range.label", "Time range")}
    >
      {RANGE_OPTIONS.map((it, idx) => {
        const isActive = it.key === active;
        const activeIndex = RANGE_OPTIONS.findIndex((item) => item.key === active);
        const showSeparator =
          idx !== RANGE_OPTIONS.length - 1 &&
          idx !== activeIndex &&
          idx + 1 !== activeIndex;

        return (
          <React.Fragment key={it.key}>
            <button
              type="button"
              className={[
                "h-9 px-5 rounded-[9px] text-body-medium transition-colors",
                isActive
                  ? "bg-[rgb(var(--clr-surface-cont-highest))] text-[rgb(var(--clr-on-surface))] shadow-[0_1px_2px_rgba(15,23,42,0.18)]"
                  : "text-[rgb(var(--clr-on-surface))] hover:bg-[rgba(var(--clr-on-surface),0.06)]",
              ].join(" ")}
              role="tab"
              aria-selected={isActive}
              onClick={() => onChange?.(it.key)}
            >
              {t(it.i18n, it.fallback)}
            </button>

            {showSeparator ? (
              <div className="w-px h-5 bg-[rgb(var(--clr-on-surface-variant))] opacity-60" />
            ) : null}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function RxStatusPill({ status }) {
  const { t } = useLanguage();
  const s = String(status || "draft").toLowerCase();
  const map = {
    draft: {
      tone: "bg-[rgba(91,107,134,0.12)] text-[rgb(var(--clr-on-surface-variant))]",
      label: t("doctorDashboard.prescriptions.status.draft", "Draft"),
    },
    issued: {
      tone: "bg-[rgba(0,189,196,0.12)] text-[#008A8F]",
      label: t("doctorDashboard.prescriptions.status.issued", "Issued"),
    },
    dispensed: {
      tone: "bg-[rgba(34,197,94,0.12)] text-[rgba(22,163,74,1)]",
      label: t("doctorDashboard.prescriptions.status.dispensed", "Dispensed"),
    },
    cancelled: {
      tone: "bg-[rgba(239,68,68,0.12)] text-[rgba(185,28,28,1)]",
      label: t("doctorDashboard.prescriptions.status.cancelled", "Cancelled"),
    },
    expired: {
      tone: "bg-[rgba(234,179,8,0.12)] text-[rgba(161,98,7,1)]",
      label: t("doctorDashboard.prescriptions.status.expired", "Expired"),
    },
  };
  const meta = map[s] || map.draft;

  return (
    <span
      className={[
        "inline-flex items-center h-7 px-3 rounded-full text-label-small leading-none",
        meta.tone,
      ].join(" ")}
      title={meta.label}
    >
      {meta.label}
    </span>
  );
}

export default function DoctorDashboardPage() {
  const auth = useAuth() || {};
  const { t, languageCode } = useLanguage();
  const userId = String(auth?.user?.id || "").trim();
  const navigate = useNavigate();
  const localeCode = React.useMemo(() => resolveUiLocale(languageCode), [languageCode]);

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const [metrics, setMetrics] = React.useState({
    patients30d: 0,
    patientsPrev30d: 0,
    rating: null,
    appointmentsThisMonth: 0,
    appointmentsPrevMonth: 0,
    prescriptions30d: 0,
    prescriptionsPrev30d: 0,
  });

  const [appointments, setAppointments] = React.useState([]);
  const [monthlySeries, setMonthlySeries] = React.useState([]);
  const [recentRx, setRecentRx] = React.useState([]);
  const [rxSearchQuery, setRxSearchQuery] = React.useState("");

  const [activeRange, setActiveRange] = React.useState("Today");
  const [visitorRangeKey, setVisitorRangeKey] = React.useState("12m");
  const [visitorMenuOpen, setVisitorMenuOpen] = React.useState(false);
  const visitorMenuRef = React.useRef(null);
  const [calendarMenuOpen, setCalendarMenuOpen] = React.useState(false);
  const calendarMenuRef = React.useRef(null);
  const [calendarMonth, setCalendarMonth] = React.useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  React.useEffect(() => {
    if (!visitorMenuOpen) return undefined;
    function onPointerDown(event) {
      if (!visitorMenuRef.current) return;
      if (!visitorMenuRef.current.contains(event.target)) {
        setVisitorMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, [visitorMenuOpen]);

  React.useEffect(() => {
    if (!calendarMenuOpen) return undefined;
    function onPointerDown(event) {
      if (!calendarMenuRef.current) return;
      if (!calendarMenuRef.current.contains(event.target)) {
        setCalendarMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, [calendarMenuOpen]);

  React.useEffect(() => {
    let alive = true;

    async function hydrateDashboard() {
      if (!userId) {
        if (alive) {
          setLoading(false);
          setError(t("doctorDashboard.errors.sessionUnavailable", "Session unavailable."));
        }
        return;
      }

      setLoading(true);
      setError("");

      try {
        const now = new Date();
        const since12Months = startOfMonth(new Date(now.getFullYear(), now.getMonth() - 11, 1));

        const doctorRes = await supabase
          .from("doctor_profiles")
          .select("id,rating")
          .eq("user_id", userId)
          .maybeSingle();
        if (doctorRes?.error) throw doctorRes.error;

        // Historical appointment rows may use the doctor profile ID while
        // newer rows use the clinician auth user ID. Both are canonical.
        const doctorReferences = Array.from(
          new Set([userId, String(doctorRes?.data?.id || "").trim()].filter(Boolean)),
        );
        const [apptRes, prescriptionResult] = await Promise.all([
          supabase
            .from("appointments")
            .select("id, patient_id, appointment_date, status, consultation_type, diagnosis_title, diagnosis_notes")
            .in("doctor_id", doctorReferences)
            .gte("appointment_date", since12Months.toISOString())
            .order("appointment_date", { ascending: false }),
          listDoctorPrescriptions(supabase, userId, { limit: 100 })
            .then((rows) => ({ rows, error: null }))
            .catch((error) => ({ rows: [], error })),
        ]);
        const doctorPrescriptionRows = prescriptionResult.rows;

        const apptRows = Array.isArray(apptRes?.data) ? apptRes.data : [];

        let identityByAppointmentId = new Map();
        try {
          identityByAppointmentId = await loadPatientIdentityContextsByAppointmentId(apptRows, supabase);
        } catch {
          // Optional identity enrichment must not zero canonical appointment
          // and prescription aggregates when it is temporarily unavailable.
        }
        let assignedIdentityByAppointmentId = new Map();
        try {
          // Reuse the appointment-bound projection from the accepted clinician
          // appointments route. Each batch respects the hosted RPC's limit.
          for (let offset = 0; offset < apptRows.length; offset += 200) {
            const batch = await loadClinicianAppointmentPatientIdentities(
              apptRows.slice(offset, offset + 200),
              supabase,
            );
            batch.forEach((identity, appointmentId) => {
              assignedIdentityByAppointmentId.set(appointmentId, identity);
            });
          }
        } catch {
          // Keep appointment counts and the existing identity fallback intact
          // if the governed identity service is temporarily unavailable.
        }
        const hydratedAppointments = apptRows.map((row) => {
          const identity = identityByAppointmentId.get(String(row?.id || "").trim()) || {};
          const assignedIdentity = assignedIdentityByAppointmentId.get(String(row?.id || "").trim()) || {};
          const patientProfile = identity?.patientProfile || {};
          const appointmentDraft = readDoctorAppointmentDraft(String(row?.id || "").trim());
          const diagnosisSource =
            pickDiagnosisTitle(
              row?.diagnosis_title,
              appointmentDraft?.diagnosisTitle,
              appointmentDraft?.diagnosis,
            ) || "";
          const riskSource =
            diagnosisSource ||
            patientProfile?.medical_conditions ||
            patientProfile?.chronic_conditions ||
            "";

          return {
            ...row,
            patient_name:
              (assignedIdentity?.patientName !== "Patient" && assignedIdentity?.patientName) ||
              identity?.patientName ||
              resolvePatientName(patientProfile) ||
              "",
            patient_avatar:
              (assignedIdentity?.patientAvatar !== PATIENT_AVATAR_PLACEHOLDER &&
                assignedIdentity?.patientAvatar) ||
              identity?.patientAvatar ||
              identity?.avatar ||
              "",
            patient_age:
              Number.isFinite(identity?.age) ? identity.age : ageFromDateOfBirth(patientProfile?.date_of_birth),
            patientProfile,
            primary_condition: deriveAppointmentCondition({ ...row, patientProfile }) || "",
            risk_level: deriveRiskLevel(riskSource) || "Normal",
            insurance_provider: String(patientProfile?.insurance_provider || "").trim() || "",
            insurance_plan: String(patientProfile?.insurance_plan || "").trim(),
          };
        });

        const { from: start30d, to: end30d } = resolveLast30DaysWindow(now);
        const prev30dStart = startOfDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 59));
        const prev30dEnd = endOfDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30));

        const thisMonthStart = startOfMonth(now);
        const prevMonthStart = startOfMonth(new Date(now.getFullYear(), now.getMonth() - 1, 1));
        const prevMonthEnd = endOfDay(new Date(now.getFullYear(), now.getMonth(), 0));

        const patients30d = countUniqueAppointmentPatients(hydratedAppointments, {
          from: start30d,
          to: end30d,
        });
        const patientsPrev30d = countUniqueAppointmentPatients(hydratedAppointments, {
          from: prev30dStart,
          to: prev30dEnd,
        });

        const appointmentsThisMonth = hydratedAppointments.filter((row) => {
          const d = toDate(row?.appointment_date);
          return !!d && d >= thisMonthStart;
        }).length;

        const appointmentsPrevMonth = hydratedAppointments.filter((row) => {
          const d = toDate(row?.appointment_date);
          return !!d && d >= prevMonthStart && d <= prevMonthEnd;
        }).length;

        const prescriptions30d = countPrescriptionsLast30Days(doctorPrescriptionRows, now);
        const prescriptionsPrev30d = (Array.isArray(doctorPrescriptionRows) ? doctorPrescriptionRows : []).filter(
          (row) => {
            const issued = toDate(row?.issuedAt);
            return !!issued && issued >= prev30dStart && issued <= prev30dEnd;
          },
        ).length;

        const nextSeries = buildMonthlySeries(hydratedAppointments, now, languageCode, localeCode);

        if (!alive) return;

        setAppointments(hydratedAppointments);
        setMonthlySeries(nextSeries);
        setMetrics({
          patients30d,
          patientsPrev30d,
          rating: doctorRes?.data?.rating == null ? null : Number(doctorRes.data.rating),
          appointmentsThisMonth,
          appointmentsPrevMonth,
          prescriptions30d,
          prescriptionsPrev30d,
        });
        setRecentRx(Array.isArray(doctorPrescriptionRows) ? doctorPrescriptionRows.slice(0, 8) : []);

        const primaryDataError =
          doctorRes?.error?.message ||
          apptRes?.error?.message ||
          prescriptionResult?.error?.message ||
          "";
        if (primaryDataError) {
          setError(t("doctorDashboard.errors.partialData", "Some dashboard data could not be loaded."));
        }
      } catch (err) {
        if (!alive) return;
        setError(t("doctorDashboard.errors.load", "Dashboard failed to load."));
      } finally {
        if (alive) setLoading(false);
      }
    }

    hydrateDashboard();

    return () => {
      alive = false;
    };
  }, [userId, t, languageCode, localeCode]);

  const appointmentsInRange = React.useMemo(() => {
    const filtered = filterByRange(appointments, activeRange, new Date());
    return [...filtered].sort((a, b) => {
      const aTime = toDate(a?.appointment_date)?.getTime() || 0;
      const bTime = toDate(b?.appointment_date)?.getTime() || 0;
      return bTime - aTime;
    });
  }, [appointments, activeRange]);

  const visitorRange = React.useMemo(
    () =>
      VISITOR_RANGE_OPTIONS.find((option) => option.key === visitorRangeKey) ||
      VISITOR_RANGE_OPTIONS[VISITOR_RANGE_OPTIONS.length - 1],
    [visitorRangeKey]
  );

  const visitorSeries = React.useMemo(() => {
    const months = Number(visitorRange?.months || 12);
    const trimmed = months >= monthlySeries.length ? monthlySeries : monthlySeries.slice(-months);
    return trimmed.map((item, idx, arr) => ({
      ...item,
      prevAppointments: Number(arr[idx - 1]?.appointments || 0),
    }));
  }, [monthlySeries, visitorRange]);

  const filteredRecentRx = React.useMemo(() => {
    const query = String(rxSearchQuery || "").trim().toLowerCase();
    if (!query) return recentRx;

    return recentRx.filter((row) =>
      [row?.patientName, row?.patientSecondary, row?.rxNo]
        .map((value) => String(value || "").toLowerCase())
        .some((value) => value.includes(query))
    );
  }, [recentRx, rxSearchQuery]);

  const visitorRangeLabel = t(visitorRange.i18n, visitorRange.fallback);
  const calendarWeekdayLabels = React.useMemo(() => {
    const custom = CUSTOM_LOCALE_LABELS[languageCode];
    return custom?.weekdaysNarrow || ["S", "M", "T", "W", "T", "F", "S"];
  }, [languageCode]);

  const visitorsTotal = React.useMemo(
    () => visitorSeries.reduce((sum, item) => sum + Number(item?.appointments || 0), 0),
    [visitorSeries]
  );

  const visitorYAxis = React.useMemo(() => {
    const maxValue = visitorSeries.reduce(
      (max, item) => Math.max(max, Number(item?.appointments || 0)),
      0
    );
    const top = Math.ceil(Math.max(1200, maxValue) / 400) * 400;
    const ticks = Array.from({ length: top / 400 + 1 }, (_, idx) => idx * 400);
    return { top, ticks };
  }, [visitorSeries]);

  const visitorTrendPct = React.useMemo(() => {
    const months = Number(visitorRange?.months || 12);
    const current = monthlySeries
      .slice(-months)
      .reduce((sum, item) => sum + Number(item?.appointments || 0), 0);
    const previous = monthlySeries
      .slice(-(months * 2), -months)
      .reduce((sum, item) => sum + Number(item?.appointments || 0), 0);
    return formatTrend(current, previous);
  }, [monthlySeries, visitorRange]);

  const calendarMonthOptions = React.useMemo(() => {
    const base = new Date();
    const options = [];
    for (let i = -24; i <= 12; i += 1) {
      const date = new Date(base.getFullYear(), base.getMonth() + i, 1);
      options.push({
        key: monthKey(date),
        label: formatMonthLong(date, languageCode, localeCode),
        date,
      });
    }
    return options.reverse();
  }, [languageCode, localeCode]);

  const calendarStats = React.useMemo(() => {
    const stats = new Map();
    const y = calendarMonth.getFullYear();
    const m = calendarMonth.getMonth();

    for (const row of appointments) {
      const d = toDate(row?.appointment_date);
      if (!d || d.getFullYear() !== y || d.getMonth() !== m) continue;

      const day = d.getDate();
      const current = stats.get(day) || { appointments: 0, eVisits: 0, pending: 0 };
      current.appointments += 1;

      if (String(row?.consultation_type || "").toLowerCase() === "private") {
        current.eVisits += 1;
      }

      if (["pending", "scheduled"].includes(String(row?.status || "").toLowerCase())) {
        current.pending += 1;
      }

      stats.set(day, current);
    }

    return stats;
  }, [appointments, calendarMonth]);

  const calendarCells = React.useMemo(() => {
    const y = calendarMonth.getFullYear();
    const m = calendarMonth.getMonth();
    const firstWeekday = new Date(y, m, 1).getDay();
    const daysInMonth = new Date(y, m + 1, 0).getDate();

    const cells = [];
    for (let i = 0; i < firstWeekday; i += 1) {
      cells.push({ key: `empty-${i}`, day: null, stats: null });
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
      cells.push({
        key: `day-${day}`,
        day,
        stats: calendarStats.get(day) || { appointments: 0, eVisits: 0, pending: 0 },
      });
    }

    const minCells = cells.length > 35 ? 42 : 35;
    while (cells.length < minCells) {
      cells.push({
        key: `empty-tail-${cells.length}`,
        day: null,
        stats: null,
      });
    }

    return cells;
  }, [calendarMonth, calendarStats]);

  const patientsValue = String(metrics.patients30d || 0);
  const ratingValue = Number.isFinite(metrics.rating)
    ? Number(metrics.rating).toFixed(1)
    : "-";
  const appointmentsValue = String(metrics.appointmentsThisMonth || 0);
  const prescriptionsValue = String(metrics.prescriptions30d || 0);
  const visitorsValue = String(visitorsTotal || 0);

  return (
    <div className="w-full">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <MetricCard
          iconSrc={ICON_PATIENTS}
          iconSize={20}
          label={t("doctorDashboard.metrics.patients", "Patients")}
          value={patientsValue}
          footerLabel={t("doctorDashboard.metrics.past30Days", "Past 30 days")}
          footerPct={formatTrend(metrics.patients30d, metrics.patientsPrev30d)}
        />

        <MetricCard
          iconSrc={ICON_RATING}
          iconSize={24}
          iconNudgeX={2}
          iconNudgeY={1}
          label={t("doctorDashboard.metrics.overallRating", "Overall rating")}
          value={ratingValue}
        />

        <MetricCard
          iconSrc={ICON_APPOINTMENTS}
          iconSize={20}
          label={t("doctorDashboard.metrics.appointments", "Appointments")}
          value={appointmentsValue}
          footerLabel={t("doctorDashboard.metrics.thisMonth", "This month")}
          footerPct={formatTrend(metrics.appointmentsThisMonth, metrics.appointmentsPrevMonth)}
          onClick={() => navigate("/doctors/appointments?view=day")}
        />

        <MetricCard
          iconSrc={ICON_PRESCRIPTIONS}
          iconSize={20}
          label={t("doctorDashboard.metrics.prescriptions", "Prescriptions")}
          value={prescriptionsValue}
          footerLabel={t("doctorDashboard.metrics.past30Days", "Past 30 days")}
          footerPct={formatTrend(metrics.prescriptions30d, metrics.prescriptionsPrev30d)}
          onClick={() =>
            navigate("/doctors/prescriptions", {
              state: { returnTo: "/doctor/dashboard" },
            })
          }
        />
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card
          className="
            md:col-span-2
            p-4
            min-h-[260px]
            flex flex-col
          "
        >
          <div className="flex items-center justify-between gap-3">
            <div className="text-title-medium text-on-surface-var">{t("doctorDashboard.visitors.title", "Clinic visitors")}</div>
            <div className="relative" ref={visitorMenuRef}>
              <RangePill
                label={visitorRangeLabel}
                expanded={visitorMenuOpen}
                onClick={() => setVisitorMenuOpen((v) => !v)}
              />

              {visitorMenuOpen ? (
                <div
                  className="
                    absolute right-0 mt-2 z-30 min-w-[196px]
                    rounded-[12px]
                    bg-[rgb(var(--clr-surface-cont-highest))]
                    border border-[rgba(24,50,119,0.10)]
                    shadow-[0px_6px_12px_-6px_rgba(29,79,215,0.10),0px_3px_6px_-3px_rgba(26,27,35,0.08)]
                    overflow-hidden
                  "
                  role="menu"
                  aria-label={t("doctorDashboard.visitors.menu", "Clinic visitors date range")}
                >
                  {VISITOR_RANGE_OPTIONS.map((option) => {
                    const selected = option.key === visitorRange.key;
                    return (
                      <button
                        key={option.key}
                        type="button"
                        role="menuitemradio"
                        aria-checked={selected}
                        onClick={() => {
                          setVisitorRangeKey(option.key);
                          setVisitorMenuOpen(false);
                        }}
                        className={[
                          "w-full h-10 px-4 text-left text-body-medium",
                          selected
                            ? "bg-[rgba(24,50,119,0.10)] text-on-surface"
                            : "text-on-surface-var hover:bg-[rgba(24,50,119,0.06)]",
                        ].join(" ")}
                      >
                        {t(option.i18n, option.fallback)}
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </div>

          <div className="mt-3 text-headline-small text-[rgb(var(--clr-on-surface))]">
            {visitorsValue}
          </div>

          <div className="mt-2 flex items-center gap-3">
            <span className="text-body-small text-[rgb(var(--clr-on-surface-variant))]">
              {visitorRangeLabel}
            </span>
            <PercentBadge value={visitorTrendPct} />
          </div>

          <div className="mt-4 flex-1 min-h-[280px]">
            {visitorSeries.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={visitorSeries} margin={{ top: 6, right: 0, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="rgba(var(--clr-outline-variant),0.55)" vertical={false} />
                  <XAxis
                    dataKey="month"
                    tickLine={false}
                    axisLine={false}
                    tick={{ fill: "rgba(91,107,134,1)", fontSize: 12 }}
                  />
                  <YAxis
                    allowDecimals={false}
                    tickLine={false}
                    axisLine={false}
                    tick={<VisitorYAxisTick />}
                    ticks={visitorYAxis.ticks}
                    domain={[0, visitorYAxis.top]}
                    width={42}
                    interval={0}
                  />
                  <Tooltip
                    cursor={{ stroke: "rgba(91,107,134,0.28)", strokeWidth: 1 }}
                    content={<VisitorsTooltip />}
                  />
                  <Line
                    type="monotone"
                    dataKey="appointments"
                    name={t("doctorDashboard.metrics.appointments", "Appointments")}
                    stroke={CHART_LINE_COLOR}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 6, strokeWidth: 2, fill: CHART_LINE_COLOR, stroke: "#FFFFFF" }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full grid place-items-center text-body-medium text-[rgb(var(--clr-on-surface-variant))]">
                {loading
                  ? t("doctorDashboard.visitors.loadingChart", "Loading chart...")
                  : t("doctorDashboard.visitors.empty", "No appointment trend data yet.")}
              </div>
            )}
          </div>
        </Card>

        <Card
          className="
            p-4
            min-h-[260px]
          "
        >
          <div className="flex items-center justify-between">
            <div className="text-title-medium text-on-surface-var">{t("doctorDashboard.calendar.title", "Consultancy visitors")}</div>
            <div />
          </div>

            <div className="mt-3 flex items-center justify-between gap-3">
              <div className="relative" ref={calendarMenuRef}>
                <MonthDropdown
                  label={formatMonthLong(calendarMonth, languageCode, localeCode)}
                  expanded={calendarMenuOpen}
                  onClick={() => setCalendarMenuOpen((v) => !v)}
                />
                {calendarMenuOpen ? (
                  <div
                    className="
                      absolute left-0 top-full mt-2 z-30 w-[220px] max-h-[256px] overflow-auto
                      rounded-[12px] bg-[rgb(var(--clr-surface-cont-highest))]
                      border border-[rgba(24,50,119,0.10)]
                      shadow-[0px_6px_12px_-6px_rgba(29,79,215,0.10),0px_3px_6px_-3px_rgba(26,27,35,0.08)]
                    "
                    role="menu"
                    aria-label={t("doctorDashboard.calendar.selectMonth", "Select month")}
                  >
                    {calendarMonthOptions.map((option) => {
                      const selected = monthKey(calendarMonth) === option.key;
                      return (
                        <button
                          key={option.key}
                          type="button"
                          role="menuitemradio"
                          aria-checked={selected}
                          onClick={() => {
                            setCalendarMonth(option.date);
                            setCalendarMenuOpen(false);
                          }}
                          className={[
                            "w-full h-10 px-4 text-left text-body-medium",
                            selected
                              ? "bg-[rgba(24,50,119,0.10)] text-on-surface"
                              : "text-on-surface-var hover:bg-[rgba(24,50,119,0.06)]",
                          ].join(" ")}
                        >
                          {formatMonthLong(option.date, languageCode, localeCode)}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>
              <CalendarNavArrows
              onPrev={() =>
                setCalendarMonth((prev) =>
                  new Date(prev.getFullYear(), prev.getMonth() - 1, 1)
                )
              }
              onNext={() =>
                setCalendarMonth((prev) =>
                  new Date(prev.getFullYear(), prev.getMonth() + 1, 1)
                )
              }
              />
            </div>

          <div className="mt-4">
            <div className="grid grid-cols-7 gap-2 mb-2">
              {calendarWeekdayLabels.map((d) => (
                <div
                  key={d}
                  className="text-center text-label-small text-[rgb(var(--clr-on-surface-variant))]"
                >
                  {d}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-2">
              {calendarCells.map((cell) => {
                if (!cell.day) {
                  return <div key={cell.key} className="h-11 rounded-[8px]" aria-hidden="true" />;
                }

                const now = new Date();
                const isToday =
                  calendarMonth.getFullYear() === now.getFullYear() &&
                  calendarMonth.getMonth() === now.getMonth() &&
                  cell.day === now.getDate();
                const segments = [
                  cell.stats?.eVisits > 0
                    ? { key: "evisit", tone: "bg-[rgba(0,189,196,0.60)]" }
                    : null,
                  cell.stats?.pending > 0
                    ? { key: "pending", tone: "bg-[rgba(86,106,139,0.38)]" }
                    : null,
                  cell.stats?.appointments > 0
                    ? { key: "appointment", tone: "bg-[rgba(27,148,218,0.60)]" }
                    : null,
                ].filter(Boolean);
                const hasData = segments.length > 0;

                return (
                  <div
                    key={cell.key}
                    className="h-11 rounded-[8px] px-1 pt-1 bg-transparent"
                        title={
                          hasData
                        ? `${cell.stats.appointments} ${t("doctorDashboard.calendar.legend.appointment", "Appointment")} · ${cell.stats.eVisits} ${t("doctorDashboard.calendar.legend.eVisit", "Virtual")} · ${cell.stats.pending} ${t("doctorDashboard.calendar.legend.pending", "Pending")}`
                        : t("doctorDashboard.calendar.noAppointments", "No appointments")
                    }
                  >
                    <div
                      className={[
                        "text-center text-label-small",
                        isToday ? "text-[rgb(var(--schemes-secondary))]" : "text-[rgb(var(--clr-on-surface))]",
                      ].join(" ")}
                    >
                      {cell.day}
                    </div>
                    <div className="mt-1 flex items-center justify-center min-h-[6px]">
                      {hasData ? (
                        <span className="inline-flex h-[6px] w-[25px] items-start gap-px overflow-hidden rounded-[5px]">
                          {segments.map((segment, index) => {
                            const isSingle = segments.length === 1;
                            const isFirst = index === 0;
                            const isLast = index === segments.length - 1;
                            const radius = isSingle
                              ? "rounded-[8px]"
                              : isFirst
                              ? "rounded-bl-[8px] rounded-tl-[8px] rounded-br-[2px] rounded-tr-[2px]"
                              : isLast
                              ? "rounded-br-[8px] rounded-tr-[8px] rounded-bl-[2px] rounded-tl-[2px]"
                              : "rounded-[2px]";
                            return (
                              <span
                                key={`${cell.key}-${segment.key}`}
                                className={["h-[6px] flex-1 min-w-0", segment.tone, radius].join(" ")}
                                aria-hidden="true"
                              />
                            );
                          })}
                        </span>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
            <LegendBadge iconSrc={ICON_LEGEND_APPOINTMENT} label={t("doctorDashboard.calendar.legend.appointment", "Appointment")} />
            <LegendBadge iconSrc={ICON_LEGEND_EVISIT} label={t("doctorDashboard.calendar.legend.eVisit", "Virtual")} />
            <LegendBadge iconSrc={ICON_LEGEND_PENDING} label={t("doctorDashboard.calendar.legend.pending", "Pending")} />
          </div>
        </Card>
      </div>

      <Card
        className="
          mt-4
          overflow-hidden
        "
      >
        <div className="p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
            <div className="order-2 sm:order-2 shrink-0 w-full sm:w-auto">
              <SegmentedControl active={activeRange} onChange={setActiveRange} />
            </div>

            <div className="order-1 sm:order-1">
              <div className="text-title-large text-[rgb(var(--clr-on-surface))]">
                {t("doctorDashboard.patientsAppointments.title", "Patients Appointments")}
              </div>
              <div className="mt-1 text-body-medium text-[rgb(var(--clr-on-surface-variant))]">
                {t(
                  "doctorDashboard.patientsAppointments.subtitle",
                  "An overview of the patient's journey and the assistance provided at the clinic."
                )}
              </div>
            </div>
          </div>

          {error ? (
            <div className="mt-3 text-body-small text-[rgb(var(--afrimed-danger))]">{error}</div>
          ) : null}
        </div>

        <div className="h-px bg-[rgba(var(--clr-outline-variant),0.55)]" />

        <div className="p-4">
          <div className="hidden md:block rounded-[12px] border border-[rgba(var(--clr-outline-variant),0.55)] overflow-hidden">
            <div className="grid grid-cols-[minmax(0,2.2fr)_minmax(0,1.7fr)_minmax(0,1.4fr)_minmax(0,1.6fr)_minmax(0,1.1fr)_28px] items-center px-4 h-9 bg-[rgba(var(--clr-on-surface),0.04)]">
              <div className="text-label-medium text-on-surface-var">{t("doctorDashboard.patientsAppointments.columns.patientName", "Patient name")}</div>
              <div className="text-label-medium text-on-surface-var">{t("doctorDashboard.patientsAppointments.columns.primaryCondition", "Primary condition")}</div>
              <div className="text-label-medium text-on-surface-var">{t("doctorDashboard.patientsAppointments.columns.riskLevel", "Risk level")}</div>
              <div className="text-label-medium text-on-surface-var">{t("doctorDashboard.patientsAppointments.columns.insurance", "Insurance")}</div>
              <div className="text-label-medium text-on-surface-var">{t("doctorDashboard.patientsAppointments.columns.lastVisit", "Last visit")}</div>
              <div aria-hidden="true" />
            </div>

            {loading ? (
              <div className="px-4 py-4 text-body-medium text-[rgb(var(--clr-on-surface-variant))]">
                {t("doctorDashboard.patientsAppointments.loading", "Loading appointments...")}
              </div>
            ) : appointmentsInRange.length === 0 ? (
              <div className="px-4 py-4 text-body-medium text-[rgb(var(--clr-on-surface-variant))]">
                {t("doctorDashboard.patientsAppointments.empty", "No appointments in this range.")}
              </div>
            ) : (
              appointmentsInRange.slice(0, 10).map((row, idx) => (
                <div key={`${row?.id || "row"}-${idx}`}>
                  <div className="grid grid-cols-[minmax(0,2.2fr)_minmax(0,1.7fr)_minmax(0,1.4fr)_minmax(0,1.6fr)_minmax(0,1.1fr)_28px] items-center gap-x-3 px-4 min-h-[56px]">
                    <div className="min-w-0">
                      <PrescriptionPatientCell
                        name={row?.patient_name || t("doctorDashboard.patientFallback", "Patient")}
                        subtitle={Number.isFinite(row?.patient_age) ? t("doctorDashboard.patientAge", "{count} years old", { count: row.patient_age }) : ""}
                        avatarUrl={row?.patient_avatar || ""}
                      />
                    </div>
                    <div className="text-body-medium text-[rgb(var(--clr-on-surface))] truncate">
                      {row?.primary_condition || t("doctorDashboard.patientsAppointments.noDiagnosis", "No diagnosis recorded")}
                    </div>
                    <div>
                      <RiskPill level={row?.risk_level} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-body-medium text-[rgb(var(--clr-on-surface))] truncate">
                        {row?.insurance_provider || t("doctorDashboard.patientsAppointments.notProvided", "Not provided")}
                      </div>
                      {String(row?.insurance_plan || "").trim() ? (
                        <div className="mt-0.5 text-label-small text-[rgb(var(--clr-on-surface-variant))] truncate">
                          {row.insurance_plan}
                        </div>
                      ) : null}
                    </div>
                    <div className="text-body-medium text-[rgb(var(--clr-on-surface))]">
                      {fmtShortDate(row?.appointment_date, languageCode, localeCode)}
                    </div>
                    <button
                      type="button"
                      onClick={() => navigate(`/doctors/appointments/${row?.id}`)}
                      className="h-7 w-7 rounded-[8px] grid place-items-center text-[rgb(var(--clr-on-surface-variant))] hover:bg-[rgba(var(--clr-on-surface),0.08)]"
                      aria-label={t("doctorDashboard.patientsAppointments.openDetails", "Open appointment details")}
                      title={t("doctorDashboard.patientsAppointments.openDetails", "Open appointment details")}
                    >
                      &#8942;
                    </button>
                  </div>
                  {idx !== Math.min(appointmentsInRange.length, 10) - 1 ? (
                    <div className="h-px bg-[rgba(var(--clr-outline-variant),0.55)]" />
                  ) : null}
                </div>
              ))
            )}
          </div>

          <div className="md:hidden">
            {loading ? (
              <div className="rounded-[12px] border border-[rgba(var(--clr-outline-variant),0.55)] px-4 py-4 text-body-medium text-[rgb(var(--clr-on-surface-variant))]">
                {t("doctorDashboard.patientsAppointments.loading", "Loading appointments...")}
              </div>
            ) : appointmentsInRange.length === 0 ? (
              <>
                <div className="mb-2 grid grid-cols-2 gap-x-3 gap-y-1 rounded-[12px] border border-[rgba(var(--clr-outline-variant),0.55)] px-3 py-2 bg-[rgba(var(--clr-on-surface),0.04)]">
                  <div className="text-label-small text-[rgb(var(--clr-on-surface-variant))]">{t("doctorDashboard.patientsAppointments.columns.patientName", "Patient name")}</div>
                  <div className="text-label-small text-[rgb(var(--clr-on-surface-variant))]">{t("doctorDashboard.patientsAppointments.columns.primaryCondition", "Primary condition")}</div>
                  <div className="text-label-small text-[rgb(var(--clr-on-surface-variant))]">{t("doctorDashboard.patientsAppointments.columns.riskLevel", "Risk level")}</div>
                  <div className="text-label-small text-[rgb(var(--clr-on-surface-variant))]">{t("doctorDashboard.patientsAppointments.columns.insurance", "Insurance")}</div>
                  <div className="text-label-small text-[rgb(var(--clr-on-surface-variant))]">{t("doctorDashboard.patientsAppointments.columns.lastVisit", "Last visit")}</div>
                </div>
                <div className="rounded-[12px] border border-[rgba(var(--clr-outline-variant),0.55)] px-4 py-4 text-body-medium text-[rgb(var(--clr-on-surface-variant))]">
                  {t("doctorDashboard.patientsAppointments.empty", "No appointments in this range.")}
                </div>
              </>
            ) : (
              <div className="space-y-3">
                {appointmentsInRange.slice(0, 10).map((row, idx) => (
                  <div
                    key={`${row?.id || "mrow"}-${idx}`}
                    className="rounded-[12px] border border-[rgba(var(--clr-outline-variant),0.55)] p-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <PrescriptionPatientCell
                          name={row?.patient_name || t("doctorDashboard.patientFallback", "Patient")}
                          subtitle={Number.isFinite(row?.patient_age) ? t("doctorDashboard.patientAge", "{count} years old", { count: row.patient_age }) : ""}
                          avatarUrl={row?.patient_avatar || ""}
                          nameClassName="text-title-small text-[rgb(var(--clr-on-surface))]"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => navigate(`/doctors/appointments/${row?.id}`)}
                        className="h-7 w-7 rounded-[8px] grid place-items-center text-[rgb(var(--clr-on-surface-variant))] hover:bg-[rgba(var(--clr-on-surface),0.08)]"
                        aria-label={t("doctorDashboard.patientsAppointments.openDetails", "Open appointment details")}
                        title={t("doctorDashboard.patientsAppointments.openDetails", "Open appointment details")}
                      >
                        &#8942;
                      </button>
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2">
                      <MobileField label={t("doctorDashboard.patientsAppointments.columns.primaryCondition", "Primary condition")}>
                        {row?.primary_condition || t("doctorDashboard.patientsAppointments.noDiagnosis", "No diagnosis recorded")}
                      </MobileField>
                      <MobileField label={t("doctorDashboard.patientsAppointments.columns.riskLevel", "Risk level")}>
                        <RiskPill level={row?.risk_level} />
                      </MobileField>
                      <MobileField label={t("doctorDashboard.patientsAppointments.columns.insurance", "Insurance")}>
                        <span className="block truncate">
                          {row?.insurance_provider || t("doctorDashboard.patientsAppointments.notProvided", "Not provided")}
                        </span>
                      </MobileField>
                      <MobileField label={t("doctorDashboard.patientsAppointments.columns.lastVisit", "Last visit")}>{fmtShortDate(row?.appointment_date, languageCode, localeCode)}</MobileField>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="h-px bg-[rgba(var(--clr-outline-variant),0.55)]" />

        <div className="p-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <div className="text-title-medium text-[rgb(var(--clr-on-surface))]">
                {t("doctorDashboard.prescriptions.title", "Recent Prescriptions")}
              </div>
              <div className="mt-1 text-body-medium text-[rgb(var(--clr-on-surface-variant))]">
                {t("doctorDashboard.prescriptions.subtitle", "Latest prescriptions you created (doctor scoped).")}
              </div>
            </div>
            <AfrimedSearchBar
              value={rxSearchQuery}
              onChange={(event) => setRxSearchQuery(event.target.value)}
              placeholder={t("doctorDashboard.prescriptions.searchPlaceholder", "Search by Patient Id")}
              className="w-full md:w-[240px]"
              ariaLabel={t("doctorDashboard.prescriptions.searchAria", "Search recent prescriptions by patient id")}
            />
          </div>

          <div className="hidden md:block mt-4 rounded-[12px] border border-[rgba(var(--clr-outline-variant),0.55)] overflow-hidden">
            <div className="grid grid-cols-12 px-4 py-3 bg-[rgba(var(--clr-on-surface),0.04)]">
              <div className="col-span-4 text-label-medium text-on-surface-var">{t("doctorDashboard.prescriptions.columns.patient", "Patient")}</div>
              <div className="col-span-2 text-label-medium text-on-surface-var">{t("doctorDashboard.prescriptions.columns.issued", "Issued")}</div>
              <div className="col-span-2 text-label-medium text-on-surface-var">{t("doctorDashboard.prescriptions.columns.expires", "Expires")}</div>
              <div className="col-span-2 text-label-medium text-on-surface-var">{t("doctorDashboard.prescriptions.columns.rxNumber", "Rx no.")}</div>
              <div className="col-span-2 text-label-medium text-on-surface-var text-right">{t("doctorDashboard.prescriptions.columns.status", "Status")}</div>
            </div>

            {loading ? (
              <div className="px-4 py-4 text-body-medium text-[rgb(var(--clr-on-surface-variant))]">
                {t("doctorDashboard.prescriptions.loading", "Loading prescriptions...")}
              </div>
            ) : filteredRecentRx.length === 0 ? (
              <div className="px-4 py-4 text-body-medium text-[rgb(var(--clr-on-surface-variant))]">
                {recentRx.length === 0
                  ? t("doctorDashboard.prescriptions.empty", "No prescriptions yet.")
                  : t("doctorDashboard.prescriptions.noMatches", "No matches found.")}
              </div>
            ) : (
              filteredRecentRx.map((row, idx) => (
                <div key={row?.id || idx}>
                  <button
                    type="button"
                    onClick={() =>
                      row?.id
                        ? navigate(`/appointments/prescriptions/${row.id}`, {
                            state: {
                              actorRole: "doctor",
                              returnTo: "/doctor/dashboard",
                            },
                          })
                        : null
                    }
                    className="grid w-full grid-cols-12 items-center px-4 py-3 text-left hover:bg-[rgba(var(--clr-on-surface),0.04)]"
                  >
                    <div className="col-span-4 min-w-0">
                      <PrescriptionPatientCell
                        name={row?.patientName || t("doctorDashboard.patientFallback", "Patient")}
                        subtitle={row?.patientSecondary || row?.rxNo || ""}
                        avatarUrl={row?.patientAvatarUrl || ""}
                      />
                    </div>
                    <div className="col-span-2 text-body-medium text-[rgb(var(--clr-on-surface-variant))]">
                      {fmtShortDate(row?.issuedAt, languageCode, localeCode)}
                    </div>
                    <div className="col-span-2 text-body-medium text-[rgb(var(--clr-on-surface-variant))]">
                      {fmtShortDate(row?.expiresAt, languageCode, localeCode)}
                    </div>
                    <div className="col-span-2 text-body-medium text-[rgb(var(--clr-on-surface-variant))]">
                      {row?.rxNo || "-"}
                    </div>
                    <div className="col-span-2 flex justify-end">
                      <RxStatusPill status={row?.status} />
                    </div>
                  </button>
                  {idx !== filteredRecentRx.length - 1 ? (
                    <div className="h-px bg-[rgba(var(--clr-outline-variant),0.55)]" />
                  ) : null}
                </div>
              ))
            )}
          </div>

          <div className="md:hidden mt-4">
            {loading ? (
              <div className="rounded-[12px] border border-[rgba(var(--clr-outline-variant),0.55)] px-4 py-4 text-body-medium text-[rgb(var(--clr-on-surface-variant))]">
                {t("doctorDashboard.prescriptions.loading", "Loading prescriptions...")}
              </div>
            ) : filteredRecentRx.length === 0 ? (
              <>
                <div className="mb-2 grid grid-cols-2 gap-x-3 gap-y-1 rounded-[12px] border border-[rgba(var(--clr-outline-variant),0.55)] px-3 py-2 bg-[rgba(var(--clr-on-surface),0.04)]">
                  <div className="text-label-small text-[rgb(var(--clr-on-surface-variant))]">{t("doctorDashboard.prescriptions.columns.patient", "Patient")}</div>
                  <div className="text-label-small text-[rgb(var(--clr-on-surface-variant))]">{t("doctorDashboard.prescriptions.columns.issued", "Issued")}</div>
                  <div className="text-label-small text-[rgb(var(--clr-on-surface-variant))]">{t("doctorDashboard.prescriptions.columns.expires", "Expires")}</div>
                  <div className="text-label-small text-[rgb(var(--clr-on-surface-variant))]">{t("doctorDashboard.prescriptions.columns.status", "Status")}</div>
                </div>
                <div className="rounded-[12px] border border-[rgba(var(--clr-outline-variant),0.55)] px-4 py-4 text-body-medium text-[rgb(var(--clr-on-surface-variant))]">
                  {recentRx.length === 0
                    ? t("doctorDashboard.prescriptions.empty", "No prescriptions yet.")
                    : t("doctorDashboard.prescriptions.noMatches", "No matches found.")}
                </div>
              </>
            ) : (
              <div className="space-y-3">
                {filteredRecentRx.map((row, idx) => (
                  <button
                    type="button"
                    onClick={() =>
                      row?.id
                        ? navigate(`/appointments/prescriptions/${row.id}`, {
                            state: {
                              actorRole: "doctor",
                              returnTo: "/doctor/dashboard",
                            },
                          })
                        : null
                    }
                    key={`rx-mobile-${row?.id || idx}`}
                    className="w-full rounded-[12px] border border-[rgba(var(--clr-outline-variant),0.55)] p-3 text-left hover:bg-[rgba(var(--clr-on-surface),0.04)]"
                  >
                    <PrescriptionPatientCell
                      name={row?.patientName || t("doctorDashboard.patientFallback", "Patient")}
                      subtitle={row?.rxNo || ""}
                      avatarUrl={row?.patientAvatarUrl || ""}
                    />
                    <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-2">
                      <MobileField label={t("doctorDashboard.prescriptions.columns.issued", "Issued")}>{fmtShortDate(row?.issuedAt, languageCode, localeCode)}</MobileField>
                      <MobileField label={t("doctorDashboard.prescriptions.columns.expires", "Expires")}>{fmtShortDate(row?.expiresAt, languageCode, localeCode)}</MobileField>
                    </div>
                    <div className="mt-2">
                      <MobileField label={t("doctorDashboard.prescriptions.columns.status", "Status")}>
                        <RxStatusPill status={row?.status} />
                      </MobileField>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
